import { useState, useEffect, useRef, useCallback } from 'react';

const HEARTBEAT_INTERVAL = 25000; // 25s
const RECONNECT_BASE = 1000;
const RECONNECT_MAX = 30000;
const MAX_RECONNECT_ATTEMPTS = 10;

/**
 * WebSocket hook dedicated to interactive terminal sessions.
 * Connects to /ws/terminal/{sessionId}?token=JWT
 *
 * Extended for Phase 3: chain execution, EDR detection, pivot scan, migration.
 */
const useTerminalWebSocket = (
  sessionId,
  {
    onOutput,
    onConnected,
    onChainStep,
    onChainComplete,
    onEDRDetected,
    onPivotMap,
    onMigrationSuggestion,
    enabled = true,
  } = {},
) => {
  const [connectionState, setConnectionState] = useState('disconnected');
  const [sessionInfo, setSessionInfo] = useState(null);
  const [error, setError] = useState(null);

  const wsRef = useRef(null);
  const heartbeatRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const enabledRef = useRef(enabled);

  // Callback refs — kept fresh without causing reconnections
  const onOutputRef = useRef(onOutput);
  const onConnectedRef = useRef(onConnected);
  const onChainStepRef = useRef(onChainStep);
  const onChainCompleteRef = useRef(onChainComplete);
  const onEDRDetectedRef = useRef(onEDRDetected);
  const onPivotMapRef = useRef(onPivotMap);
  const onMigrationSuggestionRef = useRef(onMigrationSuggestion);

  useEffect(() => { onOutputRef.current = onOutput; }, [onOutput]);
  useEffect(() => { onConnectedRef.current = onConnected; }, [onConnected]);
  useEffect(() => { onChainStepRef.current = onChainStep; }, [onChainStep]);
  useEffect(() => { onChainCompleteRef.current = onChainComplete; }, [onChainComplete]);
  useEffect(() => { onEDRDetectedRef.current = onEDRDetected; }, [onEDRDetected]);
  useEffect(() => { onPivotMapRef.current = onPivotMap; }, [onPivotMap]);
  useEffect(() => { onMigrationSuggestionRef.current = onMigrationSuggestion; }, [onMigrationSuggestion]);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  const clearHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const startHeartbeat = useCallback((ws) => {
    clearHeartbeat();
    heartbeatRef.current = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, HEARTBEAT_INTERVAL);
  }, [clearHeartbeat]);

  const connect = useCallback(() => {
    if (!sessionId || !enabledRef.current) return;

    const token = localStorage.getItem('access_token');
    if (!token) {
      setConnectionState('error');
      setError('No authentication token');
      return;
    }

    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const url = `${proto}//${host}/ws/terminal/${sessionId}?token=${token}`;

    setConnectionState('connecting');
    setError(null);

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0;
        startHeartbeat(ws);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          switch (msg.type) {
            case 'connected':
              setConnectionState('connected');
              setSessionInfo(msg.session_info);
              if (onConnectedRef.current) onConnectedRef.current(msg.session_info);
              break;

            case 'output':
              if (onOutputRef.current && msg.data) onOutputRef.current(msg.data);
              break;

            case 'error':
              setError(msg.message);
              break;

            case 'pong':
              break;

            // --- Phase 3 message types ---
            case 'chain_step':
              if (onChainStepRef.current) onChainStepRef.current(msg);
              break;

            case 'chain_complete':
              if (onChainCompleteRef.current) onChainCompleteRef.current(msg);
              break;

            case 'edr_detected':
              if (onEDRDetectedRef.current) onEDRDetectedRef.current(msg);
              break;

            case 'pivot_map':
              if (onPivotMapRef.current) onPivotMapRef.current(msg);
              break;

            case 'migration_suggestion':
              if (onMigrationSuggestionRef.current) onMigrationSuggestionRef.current(msg);
              break;

            default:
              break;
          }
        } catch (e) {
          // ignore parse errors
        }
      };

      ws.onerror = () => {
        setError('WebSocket connection error');
      };

      ws.onclose = (event) => {
        clearHeartbeat();
        wsRef.current = null;

        if (event.code === 4001) {
          setConnectionState('error');
          setError('Authentication failed');
          return;
        }
        if (event.code === 4003) {
          setConnectionState('error');
          setError('Access denied');
          return;
        }
        if (event.code === 4004) {
          setConnectionState('error');
          setError('Session inactive or closed');
          return;
        }
        if (event.code === 4005) {
          setConnectionState('error');
          setError('Metasploit service unreachable');
        }

        if (event.code === 1000) {
          setConnectionState('disconnected');
          return;
        }

        if (enabledRef.current && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current += 1;
          const delay = Math.min(
            RECONNECT_BASE * Math.pow(2, reconnectAttemptsRef.current - 1),
            RECONNECT_MAX,
          );
          setConnectionState('connecting');
          reconnectTimeoutRef.current = setTimeout(() => connect(), delay);
        } else {
          setConnectionState('error');
          setError('Connection lost');
        }
      };
    } catch (e) {
      setConnectionState('error');
      setError(e.message);
    }
  }, [sessionId, startHeartbeat, clearHeartbeat]);

  const disconnect = useCallback(() => {
    enabledRef.current = false;
    clearHeartbeat();
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
      wsRef.current = null;
    }
    setConnectionState('disconnected');
  }, [clearHeartbeat]);

  // --- Basic send helpers ---

  const sendInput = useCallback((data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'input', data }));
    }
  }, []);

  const sendResize = useCallback((cols, rows) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'resize', cols, rows }));
    }
  }, []);

  // --- Phase 3 send helpers ---

  const sendChain = useCallback((chainId) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'chain', chain_id: chainId }));
    }
  }, []);

  const sendDetectEDR = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'detect_edr' }));
    }
  }, []);

  const sendPivotScan = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'pivot_scan' }));
    }
  }, []);

  const sendSuggestMigrate = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'suggest_migrate' }));
    }
  }, []);

  // Connect on mount / sessionId change
  useEffect(() => {
    if (enabled && sessionId) {
      connect();
    }

    return () => {
      clearHeartbeat();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted');
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [sessionId]);

  return {
    connectionState,
    isConnected: connectionState === 'connected',
    sessionInfo,
    error,
    sendInput,
    sendResize,
    sendChain,
    sendDetectEDR,
    sendPivotScan,
    sendSuggestMigrate,
    connect,
    disconnect,
  };
};

export default useTerminalWebSocket;
